import { useEffect, useRef, useState } from 'react';

/**
 * Scroll progress in [0,1].
 *
 * With no ref passed, tracks whole-document progress (used by the nav bar).
 * With a ref, tracks how far the viewport has travelled through that element
 * (used by the timeline's cyan progress rail).
 *
 * One rAF-throttled listener regardless of how many items are on screen.
 */
export default function useScrollProgress(targetRef) {
  const [progress, setProgress] = useState(0);
  const frame = useRef(0);

  useEffect(() => {
    const compute = () => {
      frame.current = 0;

      if (targetRef) {
        const el = targetRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // 0 when the element's top reaches the viewport middle,
        // 1 when its bottom does.
        const total = rect.height;
        if (total <= 0) return;
        const travelled = vh * 0.5 - rect.top;
        setProgress(Math.min(1, Math.max(0, travelled / total)));
        return;
      }

      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      setProgress(scrollable <= 0 ? 0 : Math.min(1, Math.max(0, window.scrollY / scrollable)));
    };

    const onScroll = () => {
      if (frame.current) return;
      frame.current = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame.current) window.cancelAnimationFrame(frame.current);
    };
  }, [targetRef]);

  return progress;
}
